import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import ProjectDetail from "../ProjectDetail/ProjectDetail";
import "./ProjectSection.css";
import { Helmet } from "react-helmet-async";
import { CONFIG } from "../../constants/config";

const ProjectSection = ({ expandedProject, setExpandedProject }) => {
  const [ref, inView] = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.3 },
    },
  };

  const projectVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 },
    },
  };

  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [currentVariant, setCurrentVariant] = React.useState(null);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  React.useEffect(() => {
    if (expandedProject) {
      setCurrentVariant(expandedProject);
    }
  }, [expandedProject]);

  const expandVariants = {
    iplay: {
      initial: ({ isMobile }) => ({
        x: isMobile ? 0 : "-100vw",
        y: isMobile ? "-100vh" : 0,
        opacity: 0,
      }),
      animate: {
        x: 0,
        y: 0,
        opacity: 1,
        transition: { type: "spring", stiffness: 200, damping: 30 },
      },
      exit: ({ isMobile }) => ({
        x: isMobile ? 0 : "-100vw",
        y: isMobile ? "-100vh" : 0,
        opacity: 0,
        transition: { type: "spring", stiffness: 200, damping: 30 },
      }),
    },
    inotice: {
      initial: ({ isMobile }) => ({
        x: isMobile ? 0 : "100vw",
        y: isMobile ? "100vh" : 0,
        opacity: 0,
      }),
      animate: {
        x: 0,
        y: 0,
        opacity: 1,
        transition: { type: "spring", stiffness: 200, damping: 30 },
      },
      exit: ({ isMobile }) => ({
        x: isMobile ? 0 : "100vw",
        y: isMobile ? "100vh" : 0,
        opacity: 0,
        transition: { type: "spring", stiffness: 200, damping: 30 },
      }),
    },
  };

  const handleProjectClick = (project) => {
    if (!expandedProject) {
      setCurrentVariant(project);
      setExpandedProject(project);
    }
  };

  return (
    <>
      <Helmet>
        <title>서비스 소개 | {CONFIG.META.TITLE}</title>
        <meta
          name="description"
          content="I-Projects의 주요 서비스인 I-Play와 I-Notice를 소개합니다."
        />
        <meta property="og:image" content={CONFIG.DOMAIN.IMAGE_URL} />
      </Helmet>
      <motion.section
        ref={ref}
        className="section project-section"
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
      >
        <motion.div
          className={`project-half iplay ${
            expandedProject === "iplay" ? "expanded" : ""
          }`}
          variants={projectVariants}
          onClick={() => handleProjectClick("iplay")}
        >
          <motion.div className="project-content">
            <img
              src="/imgs/icons/I-Play.png"
              alt="I-PLAY"
              className="project-icon"
            />
            <h2>I-PLAY</h2>
            <div className="project-details">
              <p>인하대학교 I-Class 강의 자동 플레이어</p>
              <motion.ul>
                <motion.li>강의 자동 재생</motion.li>
                <motion.li>재생목록 기능</motion.li>
                <motion.li>빠른 수강완료 체크</motion.li>
              </motion.ul>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          className={`project-half inotice ${
            expandedProject === "inotice" ? "expanded" : ""
          }`}
          variants={projectVariants}
          onClick={() => handleProjectClick("inotice")}
        >
          <motion.div className="project-content">
            <img
              src="/imgs/icons/I-Notice.png"
              alt="I-NOTICE"
              className="project-icon"
            />
            <h2>I-NOTICE</h2>
            <div className="project-details">
              <p>인하대학교 공지사항 모아보기 서비스</p>
              <motion.ul>
                <motion.li>빠른 공지사항 확인</motion.li>
                <motion.li>선택 학과 공지 확인</motion.li>
                <motion.li>공지사항 즐겨찾기</motion.li>
              </motion.ul>
            </div>
          </motion.div>
        </motion.div>
      </motion.section>

      <AnimatePresence mode="wait">
        {expandedProject && (
          <motion.div
            key={currentVariant}
            className="project-detail-container"
            custom={{ isMobile }}
            variants={expandVariants[currentVariant]}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <ProjectDetail
              project={expandedProject}
              onClose={() => setExpandedProject(null)}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ProjectSection;
